// frontend/src/pages/VideoPage.js
import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { ArrowLeft, SkipForward, SkipBack } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import apiService from "../services/apiService";
import VideoPlayer from "../components/video/VideoPlayer";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ContractBadge from "../components/common/ContractBadge";
import LanguageBadge from "../components/common/LanguageBadge";
import ShareButton from "../components/common/ShareButton";
import PlaylistItemCard from "../components/common/PlaylistItemCard";
import useSocialMeta from "../hooks/useSocialMeta";

const VideoPage = () => {
  const { slug, videoSlug, playlistSlug } = useParams();
  const videoSlugToUse = videoSlug || slug; // Use videoSlug if in playlist route, otherwise slug
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playlist, setPlaylist] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const { t } = useLanguage();
  const { isAuthenticated, hasAccess } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Generate sharing URL - include playlist info if applicable
  const generateShareUrl = () => {
    if (playlistSlug) {
      return `/playlist/${playlistSlug}/video/${videoSlugToUse}`;
    }
    if (location.state?.playlistSlug) {
      return `/playlist/${location.state.playlistSlug}/video/${videoSlugToUse}`;
    }
    return `/video/${videoSlugToUse}`;
  };

  // Social media meta tags
  useSocialMeta({
    title: video ? t(video.title) : null,
    description: video
      ? t(video.description) || "Watch this video on burnair Academy"
      : null,
    image: video ? video.imageUrl || video.thumbnailUrl : null,
    url: generateShareUrl(),
    type: "video",
  });

  useEffect(() => {
    loadVideo();
    // Check if we came from a playlist
    if (
      location.state?.playlist &&
      location.state?.currentIndex !== undefined
    ) {
      setPlaylist(location.state.playlist);
      setCurrentIndex(location.state.currentIndex);
    }
    // If we have a playlistSlug in URL, load the playlist
    if (playlistSlug && !location.state?.playlist) {
      loadPlaylistForSharedVideo();
    }

    // Scroll to top after a small delay to ensure content has rendered
    const scrollTimer = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);

    return () => clearTimeout(scrollTimer);
  }, [videoSlugToUse, playlistSlug]);

  const loadVideo = async () => {
    try {
      console.log("Loading video with slug:", videoSlugToUse);

      // First, get video details
      const videoResponse = await apiService.getVideoBySlug(videoSlugToUse);
      console.log("Video response:", videoResponse);

      if (videoResponse.success) {
        setVideo(videoResponse.data);

        // If video doesn't include videoUrl, or user needs to get stream separately
        if (!videoResponse.data.videoUrl && videoResponse.data.hasAccess) {
          console.log("Getting video stream separately...");
          try {
            const streamResponse = await apiService.getVideoStreamBySlug(
              videoSlugToUse
            );
            console.log("Stream response:", streamResponse);

            if (streamResponse.success) {
              setVideo((prev) => ({
                ...prev,
                videoUrl: streamResponse.data.videoUrl,
              }));
            }
          } catch (streamError) {
            console.warn("Could not load video stream:", streamError);
            // Don't fail completely, just show video info without stream
          }
        }
      } else {
        setError(videoResponse.message);
      }
    } catch (error) {
      console.error("Error loading video:", error);
      setError("Failed to load video");
    } finally {
      setLoading(false);
    }
  };

  const loadPlaylistForSharedVideo = async () => {
    try {
      const playlistResponse = await apiService.getPlaylistBySlug(playlistSlug);
      if (playlistResponse.success) {
        const playlistItems = playlistResponse.data.items;
        setPlaylist(playlistItems);

        // Find the current video index in the playlist
        const videoIndex = playlistItems.findIndex(
          (item) => item.type === "video" && item.slug === videoSlugToUse
        );
        if (videoIndex !== -1) {
          setCurrentIndex(videoIndex);
        }
      }
    } catch (error) {
      console.error("Error loading playlist for shared video:", error);
    }
  };

  const handleBack = () => {
    // Check if we're in a playlist route (from URL params)
    if (playlistSlug) {
      navigate(`/playlist/${playlistSlug}`);
    }
    // Check if we came from a playlist (from state)
    else if (location.state?.playlistSlug) {
      navigate(`/playlist/${location.state.playlistSlug}`, {
        state: { categorySlug: location.state?.categorySlug },
        replace: true,
      });
    }
    // Check if we came from a category
    else if (location.state?.categorySlug) {
      navigate(`/category/${location.state.categorySlug}`, {
        replace: true,
      });
    }
    // Otherwise go back in history
    else {
      navigate(-1);
    }
  };

  const handleLoginRequired = () => {
    // Pass both state and query param for return URL
    const currentPath = location.pathname;
    navigate(`/login?returnUrl=${encodeURIComponent(currentPath)}`, {
      state: { from: currentPath },
    });
  };

  const handleVideoEnded = () => {
    // Auto-advance to next video if in playlist
    if (playlist && currentIndex < playlist.length - 1) {
      let nextIndex = currentIndex + 1;
      // Skip dividers and find next video
      while (
        nextIndex < playlist.length &&
        playlist[nextIndex].type !== "video"
      ) {
        nextIndex++;
      }
      if (nextIndex < playlist.length) {
        const nextItem = playlist[nextIndex];
        if (hasAccess(nextItem.contract, nextItem.roles_allowed)) {
          navigateToVideo(nextIndex);
        }
      }
    }
  };

  const navigateToVideo = (index) => {
    const item = playlist[index];
    if (item && item.type === "video") {
      const currentPlaylistSlug = playlistSlug || location.state?.playlistSlug;

      // Use playlist URL if slug available, otherwise fall back to direct video URL
      const targetUrl = currentPlaylistSlug
        ? `/playlist/${currentPlaylistSlug}/video/${item.slug}`
        : `/video/${item.slug}`;

      navigate(targetUrl, {
        state: {
          playlist,
          currentIndex: index,
          playlistSlug: currentPlaylistSlug,
          categorySlug: location.state?.categorySlug,
        },
      });
    }
  };

  const handlePrevious = () => {
    if (playlist && currentIndex > 0) {
      let prevIndex = currentIndex - 1;
      // Skip non-video items
      while (prevIndex >= 0 && playlist[prevIndex].type !== "video") {
        prevIndex--;
      }
      if (prevIndex >= 0) {
        navigateToVideo(prevIndex);
      }
    }
  };

  const handleNext = () => {
    if (playlist && currentIndex < playlist.length - 1) {
      let nextIndex = currentIndex + 1;
      // Skip non-video items
      while (
        nextIndex < playlist.length &&
        playlist[nextIndex].type !== "video"
      ) {
        nextIndex++;
      }
      if (nextIndex < playlist.length) {
        navigateToVideo(nextIndex);
      }
    }
  };

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white">
        <div className="p-4">
          <button
            onClick={handleBack}
            className="flex items-center text-white hover:text-[#00A3E0] mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t({ en: "Back", de: "Zurück" })}
          </button>
          <div className="text-center text-[#DA291C]">Error: {error}</div>
        </div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-xl mb-2">Video not found</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <div className="bg-black bg-opacity-90 p-4">
        <button
          onClick={handleBack}
          className="flex items-center text-white hover:text-[#00A3E0] mb-4 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          {t({ en: "Back", de: "Zurück" })}
        </button>
      </div>

      <div className="flex justify-center">
        <div className="w-full max-w-6xl">
          {/* Show video player if user has access and video URL exists */}
          {video.hasAccess && video.videoUrl && (
            <div className="relative">
              <VideoPlayer
                videoUrl={video.videoUrl}
                posterUrl={video.thumbnailUrl}
                title={t(video.title)}
                autoplay={true}
                onEnded={handleVideoEnded}
                subtitles={video.subtitles}
                videoLanguage={video.language}
                audioFiles={video.audioFiles}
              />

              {/* Playlist navigation controls */}
              {playlist && (
                <div className="bg-gray-900 p-2 sm:p-4 flex items-center justify-between">
                  <button
                    onClick={handlePrevious}
                    disabled={currentIndex <= 0}
                    className={`flex items-center text-white px-2 sm:px-4 py-2 rounded transition-colors ${
                      currentIndex > 0
                        ? "hover:bg-gray-700"
                        : "opacity-50 cursor-not-allowed"
                    }`}
                  >
                    <SkipBack className="w-5 h-5 sm:mr-2" />
                    <span className="hidden sm:inline">
                      {t({ en: "Previous", de: "Vorherige" })}
                    </span>
                  </button>

                  <span className="text-white text-sm sm:text-base">
                    {currentIndex + 1} /{" "}
                    {playlist.filter((item) => item.type === "video").length}
                  </span>

                  <button
                    onClick={handleNext}
                    disabled={currentIndex >= playlist.length - 1}
                    className={`flex items-center text-white px-2 sm:px-4 py-2 rounded transition-colors ${
                      currentIndex < playlist.length - 1
                        ? "hover:bg-gray-700"
                        : "opacity-50 cursor-not-allowed"
                    }`}
                  >
                    <span className="hidden sm:inline">
                      {t({ en: "Next", de: "Nächste" })}
                    </span>
                    <SkipForward className="w-5 h-5 sm:ml-2" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Enhanced access required UI */}
          {!video.hasAccess && (
            <div className="relative aspect-video overflow-hidden rounded-lg">
              {/* Background with poster - no blur for better teaser */}
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: `url(${
                    video.thumbnailUrl || video.imageUrl
                  })`,
                }}
              />
              <div className="absolute inset-0 bg-black bg-opacity-60" />

              {/* Content overlay */}
              <div className="relative h-full flex items-center justify-center p-8">
                <div className="text-center max-w-lg">
                  {/* Lock icon */}
                  <div className="mx-auto w-20 h-20 mb-6 rounded-full bg-white bg-opacity-10 flex items-center justify-center">
                    <svg
                      className="w-10 h-10 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  </div>


                  {/* Access message */}
                  <div className="bg-white bg-opacity-10 rounded-lg p-4 mb-6">
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {t({ en: "Access Required", de: "Zugang erforderlich" })}
                    </h3>
                    <p className="text-gray-200">
                      {video.contract === "Registered"
                        ? t({
                            en: "Sign in to watch this video",
                            de: "Melden Sie sich an, um dieses Video anzusehen",
                          })
                        : t({
                            en: `This content requires a burnair Map ${video.contract} subscription`,
                            de: `Dieser Inhalt erfordert ein burnair Map ${video.contract} Abonnement`,
                          })}
                    </p>
                  </div>

                  {/* Action buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    {!isAuthenticated ? (
                      <button
                        onClick={handleLoginRequired}
                        className="bg-[#002855] text-white px-6 py-2 rounded-md hover:bg-[#001a3d] transition-colors"
                      >
                        {t({ en: "Login", de: "Anmelden" })}
                      </button>
                    ) : (
                      <a
                        href="https://www.burnair.cloud/upgrade"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-[#00A3E0] text-white px-8 py-3 rounded-md hover:bg-[#0090c7] transition-colors font-semibold"
                      >
                        {t({
                          en: "Upgrade Subscription",
                          de: "Abonnement erweitern",
                        })}
                      </a>
                    )}
                  </div>

                </div>
              </div>
            </div>
          )}

          {/* Show loading state if user has access but no video URL yet */}
          {video.hasAccess && !video.videoUrl && (
            <div className="aspect-video bg-gray-900 flex items-center justify-center">
              <div className="text-center text-white">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00A3E0] mx-auto mb-4"></div>
                <p>
                  {t({ en: "Loading video...", de: "Video wird geladen..." })}
                </p>
              </div>
            </div>
          )}

          {/* Video information */}
          <div className="bg-white p-6">
            <div className="flex items-start justify-between mb-4">
              <h1 className="text-2xl font-bold text-[#002855]">
                {t(video.title)}
              </h1>
              <div className="flex items-center space-x-3">
                <ShareButton
                  url={generateShareUrl()}
                  title={t(video.title)}
                  description={
                    t(video.description) ||
                    "Watch this video on burnair Academy"
                  }
                  variant="icon"
                  className="flex items-center"
                />
                <ContractBadge contract={video.contract} />
              </div>
            </div>

            {video.description && (
              <p className="text-gray-600 leading-relaxed">
                {t(video.description)}
              </p>
            )}
          </div>

          {/* Playlist items below video */}
          {playlist && location.state?.playlistSlug && (
            <div className="bg-white mt-6 p-6 rounded-lg shadow-md border border-gray-200">
              <h2 className="text-xl font-semibold text-[#002855] mb-4">
                {t({ en: "Playlist Contents", de: "Wiedergabelisten-Inhalt" })}
              </h2>
              <div className="space-y-3">
                {playlist.map((item, index) => {
                  // Calculate video number (excluding dividers)
                  const videoNumber =
                    item.type === "video"
                      ? playlist
                          .slice(0, index)
                          .filter((i) => i.type === "video").length + 1
                      : -1;

                  return (
                    <PlaylistItemCard
                      key={`${item.type}-${item.id}-${index}`}
                      item={item}
                      index={index}
                      videoNumber={videoNumber}
                      isCurrentVideo={index === currentIndex}
                      onItemClick={(item, idx) => {
                        if (item.type === "video") {
                          navigateToVideo(idx);
                        }
                      }}
                      shareUrl={`/playlist/${location.state.playlistSlug}/video/${item.slug}`}
                      showShareButton={item.type === "video"}
                      variant="small"
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoPage;
